/**
 * The support chat window. It loads only on the production host, so local
 * development, previews and the e2e stack never call the provider and never
 * leave the machine.
 */
export const SUPPORT_WIDGET_HOST = "app.sportsmet.com";
export const SUPPORT_WIDGET_SRC = "https://widget.hrc.lt/v1/loader.js";
export const SUPPORT_WIDGET_KEY = "wk_hysi62nrgiy3dzw7kb4j";

/**
 * Set on the root element while the support window is open. `index.css`
 * lifts the closed button above the phone tab bar and drops the lift while
 * this is set, so the open window is placed by the provider alone (SMOKE-1,
 * QA round 3).
 */
export const SUPPORT_WIDGET_OPEN_ATTRIBUTE = "data-support-widget-open";

/**
 * The provider's command function. Before its loader runs, a page may put a
 * stand-in on `window.HrcWidget` that only queues calls on `q`. The loader
 * replays that queue when it starts, so an `on` call made first is not lost.
 */
type typeSupportWidgetCommand = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

export type typeSupportWidgetWindow = {
  HrcWidget?: typeSupportWidgetCommand | undefined;
};

// The page's `window` is passed as a plain object: `HrcWidget` is the
// provider's global, not part of the DOM's own `Window` type.
function supportWidgetCommand(page: object): typeSupportWidgetCommand {
  const win = page as typeSupportWidgetWindow;
  const existing = win.HrcWidget;
  if (typeof existing === "function") return existing;
  const queue: typeSupportWidgetCommand = (...args: unknown[]) => {
    (queue.q ??= []).push(args);
  };
  win.HrcWidget = queue;
  return queue;
}

/** Mirrors the support window's open state onto the root element. */
export function trackSupportWidgetOpenState(
  root: Pick<Element, "setAttribute" | "removeAttribute">,
  win: object,
): void {
  const command = supportWidgetCommand(win);
  command("on", "open", () => {
    root.setAttribute(SUPPORT_WIDGET_OPEN_ATTRIBUTE, "");
  });
  command("on", "close", () => {
    root.removeAttribute(SUPPORT_WIDGET_OPEN_ATTRIBUTE);
  });
}

export function loadSupportWidget(
  hostname: string,
  doc: Pick<Document, "createElement" | "body" | "documentElement">,
  win: object,
): boolean {
  if (hostname !== SUPPORT_WIDGET_HOST) {
    return false;
  }
  // Registered before the loader is added, so the queue holds the listeners
  // when the loader replays it. No `init` is queued, so the loader still
  // starts itself from the script's `data-widget-key`.
  trackSupportWidgetOpenState(doc.documentElement, win);
  const script = doc.createElement("script");
  script.src = SUPPORT_WIDGET_SRC;
  script.async = true;
  script.dataset.widgetKey = SUPPORT_WIDGET_KEY;
  doc.body.appendChild(script);
  return true;
}
